import React from 'react';
import PropTypes from 'prop-types';
import {
  BookOpen01,
  CalendarCheck01,
  CalendarDate,
  CalendarPlus01,
  ChevronRight,
  Dataflow03,
  Download01,
  FileCheck02,
  LinkExternal01,
  Mail01
} from '@opendate-app/setlist';
import { conductorTheme } from '../themeTokens';

const RESOURCE_ICONS = {
  Offer: FileCheck02,
  V3Offer: FileCheck02,
  Hold: CalendarPlus01,
  Confirm: CalendarCheck01,
  CalendarEvent: CalendarDate,
  Broadcast: Mail01,
  Download: Download01,
  UserManualArticle: BookOpen01,
  CustomCampaign: Dataflow03
};

const RESOURCE_LABELS = {
  V3Offer: 'Offer',
  CalendarEvent: 'Calendar event',
  UserManualArticle: 'Help Center',
  CustomCampaign: 'Automation'
};

const ConductorResourceCards = ({ cards, animateIn, onOpenManualArticle }) => {
  if (!cards || cards.length === 0) return null;

  return (
    <div style={{
      display: 'grid',
      gap: '8px',
      marginTop: '10px',
      marginBottom: '8px'
    }}>
      {cards.map((card, index) => {
        const Icon = RESOURCE_ICONS[card.record_type] || LinkExternal01;
        const label = RESOURCE_LABELS[card.record_type] || card.record_type;
        const isDownload = card.record_type === 'Download';
        const opensInPanel = card.record_type === 'UserManualArticle' && Boolean(onOpenManualArticle);
        const TrailingIcon = isDownload ? Download01 : opensInPanel ? ChevronRight : LinkExternal01;

        return (
          <a
            key={`${card.record_type}-${card.id}`}
            href={card.url}
            download={isDownload ? '' : undefined}
            onClick={opensInPanel ? (event) => {
              event.preventDefault();
              onOpenManualArticle(card.id);
            } : undefined}
            className={['venue-conductor-resource-card', animateIn ? 'venue-conductor-card-enter' : null].filter(Boolean).join(' ')}
            style={{
              ...(animateIn ? { animationDelay: `${index * 50}ms` } : {}),
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start',
              border: `1px solid ${conductorTheme.borderBlue}`,
              borderRadius: '8px',
              backgroundColor: conductorTheme.surface,
              padding: '10px 12px',
              color: 'inherit',
              textDecoration: 'none',
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)'
            }}
          >
            <span style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              backgroundColor: conductorTheme.accentSurfaceMuted,
              color: conductorTheme.accentText,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginTop: '1px'
            }}>
              <Icon width={14} height={14} aria-hidden="true" />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '3px'
              }}>
                <span style={{
                  fontSize: '11px',
                  fontWeight: 800,
                  color: conductorTheme.accentText,
                  textTransform: 'uppercase',
                  letterSpacing: 0
                }}>
                  {label}
                </span>
                <TrailingIcon width={10} height={10} aria-hidden="true" style={{ color: conductorTheme.iconMuted }} />
              </span>
              <span style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 700,
                color: conductorTheme.text,
                lineHeight: 1.35,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {card.title}
              </span>
              {card.description && (
                <span style={{
                  display: 'block',
                  fontSize: '12px',
                  color: conductorTheme.textMuted,
                  lineHeight: 1.35,
                  marginTop: '2px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {card.description}
                </span>
              )}
              {card.attributes && card.attributes.length > 0 && (
                <span style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px',
                  marginTop: '7px'
                }}>
                  {card.attributes.map((attribute) => (
                    <span
                      key={`${attribute.label}-${attribute.value}`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        borderRadius: '6px',
                        backgroundColor: conductorTheme.surfaceMuted,
                        color: conductorTheme.textSoft,
                        padding: '3px 7px',
                        fontSize: '11px',
                        lineHeight: 1.2
                      }}
                    >
                      <strong style={{ fontWeight: 700 }}>{attribute.label}:</strong>
                      {attribute.value}
                    </span>
                  ))}
                </span>
              )}
            </span>
          </a>
        );
      })}
    </div>
  );
};

export const resourceCardPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  record_type: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  url: PropTypes.string.isRequired,
  inline: PropTypes.bool,
  attributes: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
  }))
});

ConductorResourceCards.propTypes = {
  cards: PropTypes.arrayOf(resourceCardPropType),
  animateIn: PropTypes.bool,
  onOpenManualArticle: PropTypes.func
};

ConductorResourceCards.defaultProps = {
  cards: [],
  animateIn: false,
  onOpenManualArticle: null
};

export default ConductorResourceCards;
